import React, { useState } from 'react';
import password_on from 'assets/icons/password-on.svg';
import password_off from 'assets/icons/password-off.svg';
import axiosInstance from 'config/axiosInstance';
import { REFRESH_TOKEN, ACCESS_TOKEN } from '../../config/keywords';
import { BASE_PATH } from 'router/pathName';
import { FaSpinner } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const LoginForm: React.FC = () => {
  const { t } = useTranslation('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      // Step 1: Authenticate and get tokens
      const response = await axiosInstance.post('/v1/token', {
        email,
        password,
      });
      const { access, refresh } = response.data;

      // Step 2: Store tokens temporarily
      localStorage.setItem(REFRESH_TOKEN, refresh);
      localStorage.setItem(ACCESS_TOKEN, access);

      // Step 3: Verify role by fetching user profile
      const profileResponse = await axiosInstance.get('/v1/profile/', {
        headers: { Authorization: `Bearer ${access}` },
      });

      const userRole = profileResponse.data.role;

      if (userRole !== 'manager') {
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem(ACCESS_TOKEN);
        setErrorMessage(t('error_role_message'));
        setIsLoading(false);
      } else {
        // Step 5: If manager, proceed with login
        window.location.replace(BASE_PATH);
      }
    } catch (err: any) {
      // Handle error during authentication or profile fetching
      setErrorMessage(t('error_message'));
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setErrorMessage('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setErrorMessage('');
  };

  return (
    <form
      onSubmit={handleLogin}
      className="flex flex-col gap-4 md:gap-8 lg:gap-12 w-full mx-20"
    >
      <div className="flex flex-col">
        <span className="text-h5 font-bold">{t('login-form.title')}</span>
        <div>
          <span className="text-h4 font-extra-bold text-primary/300">
            {t('login-form.Rovquest')}
          </span>
          <span className="text-h5 font-bold">{t('login-form.title2')}</span>
        </div>
      </div>
      <div className="flex flex-col gap-6">
        <div className="w-full">
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-regular text-neutral/100"
          >
            {t('login-form.email')}
          </label>
          <input
            type="text"
            id="email"
            className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
            placeholder={t('login-form.email-holder')}
            value={email}
            onChange={handleEmailChange}
            required
          />
        </div>
        <div className="w-full relative">
          <label
            htmlFor="password"
            className="block mb-2 text-sm font-regular text-neutral/100"
          >
            {t('login-form.password')}
          </label>
          <div className="flex items-center relative mb-2">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
              placeholder={t('login-form.password-holder')}
              value={password}
              onChange={handlePasswordChange}
              required
            />
            <div
              className="absolute inset-y-0 right-0 flex items-center pr-4"
              onClick={() => setShowPassword(!showPassword)}
            >
              <img
                src={showPassword ? password_off : password_on}
                alt="password visibility toggle"
              />
            </div>
          </div>
          <div className="text-right">
            <a href="forgot-password" className="text-primary/300">
              {t('login-form.forget-password')}
            </a>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        {errorMessage && (
          <div className="bg-error/[0.1] text-error px-4 py-3 rounded-lg relative mb-4">
            <span className="block sm:inline">{errorMessage}</span>
          </div>
        )}
        <button
          type="submit"
          className={`flex w-full h-16 rounded-lg justify-center items-center ${
            isLoading ? 'bg-neutral/50 cursor-not-allowed' : 'bg-primary/300'
          } text-title text-white`}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              {t('signing-in')}
            </>
          ) : (
            t('login-form.login')
          )}
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
